import { Type, Transform } from 'class-transformer';
import {
  IsInt,
  IsNumber,
  IsIn,
  IsArray,
  IsString,
  IsDateString,
  IsUUID,
  IsNotEmpty,
  ValidateNested,
  IsOptional,
  IsBoolean,
} from 'class-validator';
import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import {
  FiltersResponse,
  Filter,
  FilterService,
  FilterType,
} from '@vk-hr-tek/core/filter';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { QueryService } from '@vk-hr-tek/core/query';

export class GetAccountCompaniesParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class DeleteCompanyExceptionsFromAccountBusinessTripsParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;

  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class UpdateCompanyBusinessTripsExceptionParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;

  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class UpdateAccountBusinessTripsExceptionsParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class GetAccountBusinessTripsExceptionsParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class DeleteCompanyExceptionsFromAccountRoleModelParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;

  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class UpdateAccountRoleModelExceptionsParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class GetAccountRoleModelExceptionsParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class UpdateAccountRoleModelParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class GetAccountRoleModelParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class UpdateAccountSettingsParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class AddAccountGroupUsersParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class DeleteAllUserAccountGroupsParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class UpdateUserAccountGroupsParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class AddAccountGroupsToUserParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class GetAccountGroupsCountersParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class CreateAccountGroupParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class GetAccountGroupsParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class GetUsersWithAccountGroupsParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class GetAccountUsersParams {
  @IsUUID()
  @IsNotEmpty()
  account_id: string;
}

export class GetAccountCompaniesQuery {
  @IsString()
  @IsOptional()
  query?: string;

  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export class GetAccountBusinessTripsExceptionsQuery {
  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export class GetAccountRoleModelExceptionsQuery {
  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export class GetAccountGroupsQuery {
  @IsBoolean()
  @IsOptional()
  include_all?: boolean;

  @IsString()
  @IsOptional()
  group_name?: string;
}

export class GetUsersWithAccountGroupsQuery {
  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;

  @IsUUID()
  @IsOptional()
  group_id?: string;

  @IsString()
  @IsOptional()
  search?: string;
}

export type AccountSettingToSync = string;

export class CompanyAccount {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsBoolean()
  @IsNotEmpty()
  role_model_enabled: boolean;

  @IsBoolean()
  @IsOptional()
  business_trips_enabled?: boolean;

  @IsIn(
    [
      'show_closest_task_widget',
      'payslips_enabled',
      'early_employee_event_access_enabled',
      'manager_starts_from_parent_unit',
      'update_managers_in_events',
      'separate_units_enabled',
      'employee_download_pdf_access',
      'import_operational_units',
      'import_legal_units',
      'business_trips_enabled',
      'search_users_enabled',
      'kontur_fallback_enabled',
      'unit_planned_vacations_percent_enabled',
    ],
    { each: true },
  )
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  skip_account_sync?: (
    | 'show_closest_task_widget'
    | 'payslips_enabled'
    | 'early_employee_event_access_enabled'
    | 'manager_starts_from_parent_unit'
    | 'update_managers_in_events'
    | 'separate_units_enabled'
    | 'employee_download_pdf_access'
    | 'import_operational_units'
    | 'import_legal_units'
    | 'business_trips_enabled'
    | 'search_users_enabled'
    | 'kontur_fallback_enabled'
    | 'unit_planned_vacations_percent_enabled'
  )[];
}

export class GetAccountCompaniesResponse {
  @ValidateNested({ each: true })
  @Type(() => CompanyAccount)
  @IsArray()
  @IsNotEmpty()
  companies: CompanyAccount[];
}

export class AccountBusinessTripsExceptionsResponse {
  @ValidateNested({ each: true })
  @Type(() => CompanyAccount)
  @IsArray()
  @IsNotEmpty()
  companies: CompanyAccount[];

  @IsInt()
  @IsNotEmpty()
  total_count: number;
}

export class AccountRoleModelExceptionsResponse {
  @ValidateNested({ each: true })
  @Type(() => CompanyAccount)
  @IsArray()
  @IsNotEmpty()
  companies: CompanyAccount[];

  @IsInt()
  @IsNotEmpty()
  total_count: number;
}

export class RoleModelAccessControlItemKeyValue {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsInt()
  @IsNotEmpty()
  value: number;
}

export class RoleModelAccessControlItem {
  @IsBoolean()
  @IsNotEmpty()
  enabled: boolean;

  @IsIn(['employees', 'managers', 'company_groups'])
  @IsString()
  @IsNotEmpty()
  type: 'employees' | 'managers' | 'company_groups';

  @IsInt()
  @IsOptional()
  value?: number;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  group_ids?: string[];
}

export class RoleModelObject {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsInt()
  @IsNotEmpty()
  id: number;

  @IsString()
  @IsOptional()
  tooltip?: string;

  @ValidateNested({ each: true })
  @Type(() => RoleModelAccessControlItem)
  @IsArray()
  @IsNotEmpty()
  access_control: RoleModelAccessControlItem[];
}

export class RoleModelDomain {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsNotEmpty()
  group_name: string;

  @IsString()
  @IsOptional()
  tooltip?: string;

  @ValidateNested({ each: true })
  @Type(() => RoleModelObject)
  @IsArray()
  @IsOptional()
  objects?: RoleModelObject[];

  @ValidateNested({ each: true })
  @Type(() => RoleModelAccessControlItemKeyValue)
  @IsArray()
  @IsNotEmpty()
  employees_values: RoleModelAccessControlItemKeyValue[];

  @ValidateNested({ each: true })
  @Type(() => RoleModelAccessControlItemKeyValue)
  @IsArray()
  @IsNotEmpty()
  managers_values: RoleModelAccessControlItemKeyValue[];
}

export class GetAccountRoleModelResponse {
  @IsBoolean()
  @IsNotEmpty()
  role_model_enabled: boolean;

  @ValidateNested({ each: true })
  @Type(() => RoleModelDomain)
  @IsArray()
  @IsNotEmpty()
  domains: RoleModelDomain[];
}

export class AccountGroupWithCount {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsInt()
  @IsNotEmpty()
  user_count: number;
}

export class AccountGroupsWithCount {
  @ValidateNested({ each: true })
  @Type(() => AccountGroupWithCount)
  @IsArray()
  @IsNotEmpty()
  groups: AccountGroupWithCount[];
}

export class Group {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsUUID()
  @IsNotEmpty()
  parent_id: string;
}

export class Groups {
  @ValidateNested({ each: true })
  @Type(() => Group)
  @IsArray()
  @IsNotEmpty()
  groups: Group[];
}

export class GroupItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class ProfileItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export type CryptoProPluginOpenType = string;

export class CompanyItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsOptional()
  tsp_url?: string;

  @IsIn(['capi', 'cades'])
  @IsString()
  @IsOptional()
  crypto_pro_plugin_open_type?: 'capi' | 'cades';

  @IsIn(['api', 'browser_plugin'])
  @IsString()
  @IsOptional()
  hash_source?: 'api' | 'browser_plugin';
}

export class GroupUserEmployee {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => CompanyItem)
  @IsNotEmpty()
  company: CompanyItem;

  @IsString()
  @IsNotEmpty()
  personnel_number: string;
}

export class GroupUser {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  first_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  last_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  middle_name: string;

  @ValidateNested({ each: true })
  @Type(() => GroupUserEmployee)
  @IsArray()
  @IsNotEmpty()
  employees: GroupUserEmployee[];

  @ValidateNested({ each: true })
  @Type(() => ProfileItem)
  @IsArray()
  @IsOptional()
  profiles?: ProfileItem[];

  @IsBoolean()
  @IsNotEmpty()
  editable: boolean;
}

export class UserAccountGroupsItem {
  @ValidateNested()
  @Type(() => GroupUser)
  @IsNotEmpty()
  user: GroupUser;

  @ValidateNested({ each: true })
  @Type(() => GroupItem)
  @IsArray()
  @IsNotEmpty()
  groups: GroupItem[];
}

export class UsersAccountGroups {
  @ValidateNested({ each: true })
  @Type(() => UserAccountGroupsItem)
  @IsArray()
  @IsNotEmpty()
  items: UserAccountGroupsItem[];

  @IsInt()
  @IsNotEmpty()
  total_count: number;
}

export type EmployeeStatus = string;

export class UserEmployeeListItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => CompanyItem)
  @IsNotEmpty()
  company: CompanyItem;

  @IsString()
  @IsNotEmpty()
  personnel_number: string;

  @IsDateString()
  @IsOptional()
  dismissed_at?: string;

  @IsString()
  @IsNotEmpty()
  position: string;

  @IsString()
  @IsNotEmpty()
  unit: string;

  @IsIn([
    'data_received',
    'documents_verification',
    'unep_application_signing',
    'unep_releasing',
    'unep_release_failed',
    'unep_release_paused',
    'invite_sent',
    'invite_expired',
    'active',
    'dismissed',
    'wait_registration',
  ])
  @IsString()
  @IsNotEmpty()
  status:
    | 'data_received'
    | 'documents_verification'
    | 'unep_application_signing'
    | 'unep_releasing'
    | 'unep_release_failed'
    | 'unep_release_paused'
    | 'invite_sent'
    | 'invite_expired'
    | 'active'
    | 'dismissed'
    | 'wait_registration';

  @IsDateString()
  @IsNotEmpty()
  status_changed_at: string;

  @IsDateString()
  @IsNotEmpty()
  employee_created_at: string;

  @IsBoolean()
  @IsOptional()
  has_invite?: boolean;
}

export class UserListItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  first_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  middle_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  last_name: string;

  @ValidateNested({ each: true })
  @Type(() => UserEmployeeListItem)
  @IsArray()
  @IsNotEmpty()
  employees: UserEmployeeListItem[];
}

export class UserListResponse {
  @ValidateNested({ each: true })
  @Type(() => UserListItem)
  @IsArray()
  @IsNotEmpty()
  users: UserListItem[];
}

export class AccountItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class EditableAccount {
  @ValidateNested()
  @Type(() => AccountItem)
  @IsNotEmpty()
  account_item: AccountItem;

  @IsBoolean()
  @IsOptional()
  import_operational_units?: boolean;

  @IsBoolean()
  @IsOptional()
  import_legal_units?: boolean;

  @IsBoolean()
  @IsNotEmpty()
  payslips_enabled: boolean;

  @IsBoolean()
  @IsOptional()
  role_model_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  update_managers_in_events?: boolean;

  @IsBoolean()
  @IsOptional()
  early_employee_event_access_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  manager_starts_from_parent_unit?: boolean;

  @IsBoolean()
  @IsNotEmpty()
  separate_units_enabled: boolean;

  @IsBoolean()
  @IsOptional()
  employee_download_pdf_access?: boolean;

  @IsBoolean()
  @IsOptional()
  show_closest_task_widget?: boolean;

  @IsBoolean()
  @IsOptional()
  business_trips_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  account_role_model_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  account_role_excluded?: boolean;

  @IsBoolean()
  @IsOptional()
  search_users_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  kontur_fallback_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  unit_planned_vacations_percent_enabled?: boolean;
}

export class AccountListResponse {
  @ValidateNested({ each: true })
  @Type(() => EditableAccount)
  @IsArray()
  @IsNotEmpty()
  accounts: EditableAccount[];
}

export class Account {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsBoolean()
  @IsNotEmpty()
  role_model_enabled: boolean;
}

export class GetAccountsResponse {
  @ValidateNested({ each: true })
  @Type(() => Account)
  @IsArray()
  @IsNotEmpty()
  accounts: Account[];
}

export class UpdateCompanyBusinessTripsExceptionRequest {
  @IsBoolean()
  @IsNotEmpty()
  business_trips_enabled: boolean;
}

export class AccountBusinessTripsExceptionsRequest {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  company_ids: string[];
}

export class AccountRoleModelExceptionsRequest {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  company_ids: string[];
}

export class UpdateAccountRoleModelItem {
  @IsInt()
  @IsNotEmpty()
  object_id: number;

  @IsInt()
  @IsNotEmpty()
  employees: number;

  @IsInt()
  @IsNotEmpty()
  managers: number;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  company_groups?: string[];
}

export class UpdateAccountRoleModelRequest {
  @ValidateNested({ each: true })
  @Type(() => UpdateAccountRoleModelItem)
  @IsArray()
  @IsNotEmpty()
  values: UpdateAccountRoleModelItem[];
}

export class UpdateAccountSettingsRequest {
  @IsBoolean()
  @IsOptional()
  import_operational_units?: boolean;

  @IsBoolean()
  @IsOptional()
  import_legal_units?: boolean;

  @IsBoolean()
  @IsOptional()
  payslips_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  update_managers_in_events?: boolean;

  @IsBoolean()
  @IsOptional()
  manager_starts_from_parent_unit?: boolean;

  @IsBoolean()
  @IsOptional()
  role_model_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  early_employee_event_access_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  separate_units_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  employee_download_pdf_access?: boolean;

  @IsBoolean()
  @IsOptional()
  show_closest_task_widget?: boolean;

  @IsBoolean()
  @IsOptional()
  business_trips_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  search_users_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  kontur_fallback_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  unit_planned_vacations_percent_enabled?: boolean;

  @IsIn(
    [
      'show_closest_task_widget',
      'payslips_enabled',
      'early_employee_event_access_enabled',
      'manager_starts_from_parent_unit',
      'update_managers_in_events',
      'separate_units_enabled',
      'employee_download_pdf_access',
      'import_operational_units',
      'import_legal_units',
      'business_trips_enabled',
      'search_users_enabled',
      'kontur_fallback_enabled',
      'unit_planned_vacations_percent_enabled',
    ],
    { each: true },
  )
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  force_sync?: (
    | 'show_closest_task_widget'
    | 'payslips_enabled'
    | 'early_employee_event_access_enabled'
    | 'manager_starts_from_parent_unit'
    | 'update_managers_in_events'
    | 'separate_units_enabled'
    | 'employee_download_pdf_access'
    | 'import_operational_units'
    | 'import_legal_units'
    | 'business_trips_enabled'
    | 'search_users_enabled'
    | 'kontur_fallback_enabled'
    | 'unit_planned_vacations_percent_enabled'
  )[];
}

export class AddAccountGroupUsersRequest {
  @IsUUID()
  @IsNotEmpty()
  group_id: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  user_ids: string[];
}

export class DeleteAllUserGroupsRequest {
  @IsUUID()
  @IsNotEmpty()
  user_id: string;
}

export class UpdateUserAccountGroupsRequest {
  @IsUUID()
  @IsNotEmpty()
  user_id: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  group_ids: string[];
}

export class AddUserAccountGroupsRequest {
  @IsUUID()
  @IsNotEmpty()
  user_id: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  group_ids: string[];
}

export class CreateAccountGroupRequest {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsUUID()
  @IsOptional()
  parent_id?: string;
}

export class AccountUserListRequestFilter {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;
}

export class AccountUserListRequest {
  @ValidateNested()
  @Type(() => AccountUserListRequestFilter)
  @IsNotEmpty()
  filter: AccountUserListRequestFilter;

  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

@injectable()
export class AccountService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private query: QueryService,
  ) {}

  async getAccountCompanies({
    query = {},
    params,
  }: {
    query?: GetAccountCompaniesQuery;
    params: GetAccountCompaniesParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetAccountCompaniesQuery),
      this.validator.validateOrReject(params, GetAccountCompaniesParams),
    ]);

    let requestPath = '/account/{account_id}/companies';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetAccountCompaniesParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetAccountCompaniesResponse,
    );

    return response;
  }

  async deleteCompanyExceptionsFromAccountBusinessTrips({
    params,
  }: {
    params: DeleteCompanyExceptionsFromAccountBusinessTripsParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        DeleteCompanyExceptionsFromAccountBusinessTripsParams,
      ),
    ]);

    let requestPath =
      '/account/{account_id}/business_trips/exceptions/{company_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof DeleteCompanyExceptionsFromAccountBusinessTripsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.delete(requestPath, {}, { withSide: true });

    return result;
  }

  async updateCompanyBusinessTripsException({
    params,
    body,
  }: {
    params: UpdateCompanyBusinessTripsExceptionParams;
    body: UpdateCompanyBusinessTripsExceptionRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        UpdateCompanyBusinessTripsExceptionParams,
      ),
      this.validator.validateOrReject(
        body,
        UpdateCompanyBusinessTripsExceptionRequest,
      ),
    ]);

    let requestPath =
      '/account/{account_id}/business_trips/exceptions/{company_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof UpdateCompanyBusinessTripsExceptionParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, { withSide: true });

    return result;
  }

  async updateAccountBusinessTripsExceptions({
    params,
    body,
  }: {
    params: UpdateAccountBusinessTripsExceptionsParams;
    body: AccountBusinessTripsExceptionsRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        UpdateAccountBusinessTripsExceptionsParams,
      ),
      this.validator.validateOrReject(
        body,
        AccountBusinessTripsExceptionsRequest,
      ),
    ]);

    let requestPath = '/account/{account_id}/business_trips/exceptions';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof UpdateAccountBusinessTripsExceptionsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, { withSide: true });

    return result;
  }

  async getAccountBusinessTripsExceptions({
    query = {},
    params,
  }: {
    query?: GetAccountBusinessTripsExceptionsQuery;
    params: GetAccountBusinessTripsExceptionsParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetAccountBusinessTripsExceptionsQuery,
      ),
      this.validator.validateOrReject(
        params,
        GetAccountBusinessTripsExceptionsParams,
      ),
    ]);

    let requestPath = '/account/{account_id}/business_trips/exceptions';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetAccountBusinessTripsExceptionsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      AccountBusinessTripsExceptionsResponse,
    );

    return response;
  }

  async deleteCompanyExceptionsFromAccountRoleModel({
    params,
  }: {
    params: DeleteCompanyExceptionsFromAccountRoleModelParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        DeleteCompanyExceptionsFromAccountRoleModelParams,
      ),
    ]);

    let requestPath =
      '/account/{account_id}/role_model/exceptions/{company_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof DeleteCompanyExceptionsFromAccountRoleModelParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.delete(requestPath, {}, { withSide: true });

    return result;
  }

  async updateAccountRoleModelExceptions({
    params,
    body,
  }: {
    params: UpdateAccountRoleModelExceptionsParams;
    body: AccountRoleModelExceptionsRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        UpdateAccountRoleModelExceptionsParams,
      ),
      this.validator.validateOrReject(body, AccountRoleModelExceptionsRequest),
    ]);

    let requestPath = '/account/{account_id}/role_model/exceptions';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof UpdateAccountRoleModelExceptionsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, { withSide: true });

    return result;
  }

  async getAccountRoleModelExceptions({
    query = {},
    params,
  }: {
    query?: GetAccountRoleModelExceptionsQuery;
    params: GetAccountRoleModelExceptionsParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetAccountRoleModelExceptionsQuery,
      ),
      this.validator.validateOrReject(
        params,
        GetAccountRoleModelExceptionsParams,
      ),
    ]);

    let requestPath = '/account/{account_id}/role_model/exceptions';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetAccountRoleModelExceptionsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      AccountRoleModelExceptionsResponse,
    );

    return response;
  }

  async updateAccountRoleModel({
    params,
    body,
  }: {
    params: UpdateAccountRoleModelParams;
    body: UpdateAccountRoleModelRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdateAccountRoleModelParams),
      this.validator.validateOrReject(body, UpdateAccountRoleModelRequest),
    ]);

    let requestPath = '/account/{account_id}/role_model';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof UpdateAccountRoleModelParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, { withSide: true });

    return result;
  }

  async getAccountRoleModel({ params }: { params: GetAccountRoleModelParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetAccountRoleModelParams),
    ]);

    let requestPath = '/account/{account_id}/role_model';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetAccountRoleModelParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetAccountRoleModelResponse,
    );

    return response;
  }

  async updateAccountSettings({
    params,
    body = {},
  }: {
    params: UpdateAccountSettingsParams;
    body?: UpdateAccountSettingsRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdateAccountSettingsParams),
      this.validator.validateOrReject(body, UpdateAccountSettingsRequest),
    ]);

    let requestPath = '/account/{account_id}/settings';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof UpdateAccountSettingsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, { withSide: true });

    return result;
  }

  async addAccountGroupUsers({
    params,
    body,
  }: {
    params: AddAccountGroupUsersParams;
    body: AddAccountGroupUsersRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, AddAccountGroupUsersParams),
      this.validator.validateOrReject(body, AddAccountGroupUsersRequest),
    ]);

    let requestPath = '/account/{account_id}/group/users';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof AddAccountGroupUsersParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    return result;
  }

  async deleteAllUserAccountGroups({
    params,
    body,
  }: {
    params: DeleteAllUserAccountGroupsParams;
    body: DeleteAllUserGroupsRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DeleteAllUserAccountGroupsParams),
      this.validator.validateOrReject(body, DeleteAllUserGroupsRequest),
    ]);

    let requestPath = '/account/{account_id}/user/groups';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof DeleteAllUserAccountGroupsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.delete(requestPath, body, {
      withSide: true,
    });

    return result;
  }

  async updateUserAccountGroups({
    params,
    body,
  }: {
    params: UpdateUserAccountGroupsParams;
    body: UpdateUserAccountGroupsRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdateUserAccountGroupsParams),
      this.validator.validateOrReject(body, UpdateUserAccountGroupsRequest),
    ]);

    let requestPath = '/account/{account_id}/user/groups';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof UpdateUserAccountGroupsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, { withSide: true });

    return result;
  }

  async addAccountGroupsToUser({
    params,
    body,
  }: {
    params: AddAccountGroupsToUserParams;
    body: AddUserAccountGroupsRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, AddAccountGroupsToUserParams),
      this.validator.validateOrReject(body, AddUserAccountGroupsRequest),
    ]);

    let requestPath = '/account/{account_id}/user/groups';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof AddAccountGroupsToUserParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    return result;
  }

  async getAccountGroupsCounters({
    params,
  }: {
    params: GetAccountGroupsCountersParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetAccountGroupsCountersParams),
    ]);

    let requestPath = '/account/{account_id}/groups/counters';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetAccountGroupsCountersParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      AccountGroupsWithCount,
    );

    return response;
  }

  async createAccountGroup({
    params,
    body,
  }: {
    params: CreateAccountGroupParams;
    body: CreateAccountGroupRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, CreateAccountGroupParams),
      this.validator.validateOrReject(body, CreateAccountGroupRequest),
    ]);

    let requestPath = '/account/{account_id}/groups';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof CreateAccountGroupParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    return result;
  }

  async getAccountGroups({
    query = {},
    params,
  }: {
    query?: GetAccountGroupsQuery;
    params: GetAccountGroupsParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetAccountGroupsQuery),
      this.validator.validateOrReject(params, GetAccountGroupsParams),
    ]);

    let requestPath = '/account/{account_id}/groups';

    const paramsKeys = Object.keys(params) as (keyof GetAccountGroupsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(result, Groups);

    return response;
  }

  async getUsersWithAccountGroups({
    query = {},
    params,
  }: {
    query?: GetUsersWithAccountGroupsQuery;
    params: GetUsersWithAccountGroupsParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetUsersWithAccountGroupsQuery),
      this.validator.validateOrReject(params, GetUsersWithAccountGroupsParams),
    ]);

    let requestPath = '/account/{account_id}/users/groups';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetUsersWithAccountGroupsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      UsersAccountGroups,
    );

    return response;
  }

  async getAccountUsers({
    params,
    body,
  }: {
    params: GetAccountUsersParams;
    body: AccountUserListRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetAccountUsersParams),
      this.validator.validateOrReject(body, AccountUserListRequest),
    ]);

    let requestPath = '/account/{account_id}/user/list';

    const paramsKeys = Object.keys(params) as (keyof GetAccountUsersParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      UserListResponse,
    );

    return response;
  }

  async getAccountsFindByGroupAdmin() {
    let requestPath = '/account/list/find_by_group_admin';

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      AccountListResponse,
    );

    return response;
  }

  async getAccounts() {
    let requestPath = '/accounts';

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetAccountsResponse,
    );

    return response;
  }
}
